import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { listAbonnementen } from "@/features/abonnementen/abonnementen.api";
import { Status } from "@/components/ui/Status";
import { fmtMoney, fmtDate } from "@/lib/format";
import { applyParamUpdates } from "@/lib/searchParams";
import type { AbonnementStatus } from "@/lib/types";

interface Search {
  q?: string;
  status?: string;
  page?: string;
}

export const Route = createFileRoute("/_shell/abonnementen/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    q: typeof s["q"] === "string" ? (s["q"] as string) : undefined,
    status: typeof s["status"] === "string" ? (s["status"] as string) : undefined,
    page: typeof s["page"] === "string" ? (s["page"] as string) : undefined,
  }),
  component: AbonnementenPage,
});

const VIEWS: { key: string; labelKey: string }[] = [
  { key: "", labelKey: "abonnementen.view.alle" },
  { key: "actief", labelKey: "abonnementen.view.actief" },
  { key: "proef", labelKey: "abonnementen.view.proef" },
  { key: "verlenging", labelKey: "abonnementen.view.verlenging" },
  { key: "betaling", labelKey: "abonnementen.view.betaling" },
];

function AbonnementenPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const page = Number(search.page ?? "1") || 1;
  const query = useQuery({
    queryKey: ["abonnementen", { q: search.q, status: search.status, page }],
    queryFn: () =>
      listAbonnementen({ q: search.q, status: search.status, page: String(page), pageSize: "25" }),
    placeholderData: keepPreviousData,
  });

  const result = query.data;
  const rows = result?.data ?? [];
  const totalPages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  function update(updates: Record<string, string>) {
    void navigate({ search: (prev) => applyParamUpdates(prev, updates) });
    setSelected(new Set());
  }

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div className="app-page" data-screen-label="Backend Abonnementen">
      <div className="page-header">
        <div>
          <div className="page-header__title">
            <h1>{t("abonnementen.title")}</h1>
            {result && <span className="meta">{t("common.results", { count: result.total })}</span>}
          </div>
        </div>
        <div className="page-header__actions">
          <Link to="/abonnementen/new" className="dtw-btn dtw-btn--accent">
            <Icon name="plus" size={14} /> {t("action.newAbonnement")}
          </Link>
        </div>
      </div>

      <div className="toolbar">
        <div className="views">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className={`view-chip${(search.status ?? "") === v.key ? " is-active" : ""}`}
              onClick={() => update({ status: v.key })}
            >
              {t(v.labelKey as never)}
            </button>
          ))}
        </div>
        <span className="dtw-spacer" />
        <div className="dtw-input-group" style={{ width: "240px", height: "var(--control-height-sm)" }}>
          <span className="dtw-input-group__icon">
            <Icon name="search" size={13} />
          </span>
          <input
            placeholder={t("abonnementen.search")}
            defaultValue={search.q ?? ""}
            onChange={(e) => update({ q: e.target.value })}
          />
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bulkbar">
          <span className="count">{t("abonnementen.selected", { count: selected.size })}</span>
          <span className="dtw-divider--vertical" style={{ background: "rgba(255,255,255,0.2)" }} />
          <button className="dtw-btn dtw-btn--ghost dtw-btn--sm">
            <Icon name="refresh-cw" size={13} /> {t("abonnement.changeStatus")}
          </button>
          <span className="dtw-spacer" />
          <button className="dtw-btn dtw-btn--ghost dtw-btn--sm" onClick={() => setSelected(new Set())}>
            {t("action.cancel")}
          </button>
        </div>
      )}

      <div className="panel">
        {rows.length === 0 && !query.isLoading ? (
          <div className="empty">
            <div className="empty__icon">
              <Icon name="refresh-cw" size={22} />
            </div>
            <h3>{t("abonnementen.empty.title")}</h3>
            <p>{t("abonnementen.empty.body")}</p>
          </div>
        ) : (
          <div className="panel__body flush">
            <div className="dtw-table-wrap">
              <table className="dtw-table">
                <thead>
                  <tr>
                    <th style={{ width: "var(--space-8)" }} />
                    <th>{t("abonnementen.col.abonnement")}</th>
                    <th>{t("abonnementen.col.pakket")}</th>
                    <th>{t("abonnementen.col.status")}</th>
                    <th>{t("abonnementen.col.startdatum")}</th>
                    <th>{t("abonnementen.col.verlengtOp")}</th>
                    <th className="num">{t("abonnementen.col.maandbedrag")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className={selected.has(a.id) ? "is-selected" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          className="ck"
                          checked={selected.has(a.id)}
                          onChange={() => toggleRow(a.id)}
                          aria-label={t("action.new")}
                        />
                      </td>
                      <td>
                        <Link
                          to="/abonnementen/$id"
                          params={{ id: a.nummer }}
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          <div className="dtw-table__cell-stack">
                            <span className="primary mono">{a.nummer}</span>
                            <span className="secondary t-tertiary">
                              {a.klant.naam}
                              {a.klant.plaats ? ` · ${a.klant.plaats}` : ""}
                            </span>
                          </div>
                        </Link>
                      </td>
                      {/* Een abonnement is een verzameling onderdelen; in de
                          werklijst tellen de productgroepen, niet elke code. */}
                      <td>
                        <div className="chip-row">
                          {a.onderdelenSamenvatting.productgroepen.map((g) => (
                            <span className="dtw-chip dtw-chip--indigo" key={g}>
                              {a.onderdelen.find((o) => o.pakket.productgroep === g)?.pakket
                                .productgroepLabel ?? g}
                            </span>
                          ))}
                          {a.onderdelenSamenvatting.aantal === 0 && (
                            <span className="t-tertiary">{t("common.none")}</span>
                          )}
                          {a.onderdelenSamenvatting.aantalWachtOpIngangsdatum > 0 && (
                            <span className="dtw-chip" title={t("onderdelen.wachtOpDatumBody")}>
                              <Icon name="clock" size={12} />{" "}
                              {a.onderdelenSamenvatting.aantalWachtOpIngangsdatum}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Status status={a.status as AbonnementStatus} />
                      </td>
                      <td className="mono">{fmtDate(a.startdatum)}</td>
                      <td className="mono">{fmtDate(a.verlengtOp)}</td>
                      <td className="num mono">{fmtMoney(a.maandbedragCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {result && result.total > 0 && (
          <div className="panel__foot dtw-hstack">
            <span>{t("common.results", { count: result.total })}</span>
            <span className="dtw-spacer" />
            <div className="dtw-hstack" style={{ gap: "var(--space-1)" }}>
              <button
                className="dtw-btn dtw-btn--ghost dtw-btn--icon dtw-btn--sm"
                aria-label={t("common.previous")}
                disabled={page <= 1}
                onClick={() => update({ page: String(page - 1) })}
              >
                <Icon name="chevron-left" size={14} />
              </button>
              <span className="t-mono" style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)" }}>
                {page} / {totalPages}
              </span>
              <button
                className="dtw-btn dtw-btn--ghost dtw-btn--icon dtw-btn--sm"
                aria-label={t("common.next")}
                disabled={page >= totalPages}
                onClick={() => update({ page: String(page + 1) })}
              >
                <Icon name="chevron-right" size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
